import { useOktaAuth } from "@okta/okta-react";
import { PaperCompanyValues } from "enums/PaperCompanyValues";
import React, { useContext, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { CPContext } from "../../_helpers/CPContext";
import useFetch from "../../_helpers/useFetch";
import usePaperCompanyValues from "../../_helpers/usePaperCompanyValues";
import useUtilities from "../../_helpers/useUtilities";

interface SiteStatusResult {
    lastUpdated: string;
    dbHasRecords: boolean;
}

const Footer: React.FC = () => {
    const cpContext = useContext(CPContext);
    const { authState, oktaAuth } = useOktaAuth();
    const [siteStatus, setSiteStatus] = useState("");
    const navigate = useHistory();
    const { get } = useFetch();
    const { isMaintenanceError } = useUtilities();
    // eslint-disable-next-line
    const renderSiteStatus = (props: any) => <Tooltip {...props}>{siteStatus}</Tooltip>;
    const {
        companyName,
        footerParagraphOne,
        footerParagraphTwo,
        portalTermsOfUseLink,
        websiteTermsOfUseLink,
        privacyLink,
        websiteLinkingAgreementLink,
        sitemapLink,
        siteStatusLink,
        isLoading
    } = usePaperCompanyValues({
        companyName: { key: PaperCompanyValues.CompanyName },
        footerParagraphOne: { key: PaperCompanyValues.CustomerPortalFooterParagraphOne },
        footerParagraphTwo: { key: PaperCompanyValues.CustomerPortalFooterParagraphTwo },
        portalTermsOfUseLink: { key: PaperCompanyValues.PortalTermsOfUseUrl },
        websiteTermsOfUseLink: { key: PaperCompanyValues.WebsiteTermsOfUseUrl },
        privacyLink: { key: PaperCompanyValues.PrivacyPolicyUrl },
        websiteLinkingAgreementLink: {
            key: PaperCompanyValues.CustomerPortalWebsiteLinkingAgreementLink
        },
        sitemapLink: { key: PaperCompanyValues.CustomerPortalSitemapLink },
        siteStatusLink: { key: PaperCompanyValues.CustomerPortalSiteStatusLink }
    });

    const fetchSiteStatus = async () => {
        try {
            const result = await get<SiteStatusResult>(
                "/home/sitestatus",

                ""
            );
            if (result && result.lastUpdated) {
                if (cpContext.isAdmin || cpContext.isHO) {
                    setSiteStatus(
                        "Last updated: " +
                        result.lastUpdated +
                        " Policies Loaded: " +
                        result.dbHasRecords
                    );
                } else {
                    setSiteStatus("Last updated: " + result.lastUpdated);
                }
            } else {
                setSiteStatus("Last updated: NA");
            }
        } catch (e: unknown) {
            if (isMaintenanceError(e)) {
                navigate.push("/maint");
            } else {
                setSiteStatus("Last updated: NA");
            }
        }
    };

    const getCurrentYear = () => {
        const currentDate = new Date();
        return currentDate.getFullYear();
    };

    return (
        <>
            {!isLoading && (
                <footer role="contentinfo">
                    <nav className="subFooter">
                        <ul className="container" style={{ listStyleType: "none" }}>
                            <li className="copyright">
                                <p>
                                    © 2018-{getCurrentYear()} {companyName}&nbsp;{" "}
                                    <ul id="footerLinks">
                                        <li>
                                            <a href={`${portalTermsOfUseLink}`} target="_blank" rel="noopener noreferrer">Portal Terms of Use</a>{" "}
                                        </li>
                                        <li>
                                            <a className="divider" aria-hidden={true}>
                                                |
                                            </a>{" "}
                                        </li>
                                        <li>
                                            <a href={`${privacyLink}`} target="_blank" rel="noopener noreferrer">Privacy Policy</a>{" "}
                                        </li>
                                        <li>
                                            <a className="divider" aria-hidden={true}>
                                                |
                                            </a>{" "}
                                        </li>
                                        <li>
                                            <a href={`${websiteLinkingAgreementLink}`} target="_blank" rel="noopener noreferrer">
                                                Website Linking Agreement
                                            </a>{" "}
                                        </li>
                                        <li>
                                            <a className="divider" aria-hidden={true}>
                                                |
                                            </a>{" "}
                                        </li>
                                        <li>
                                            <a href={`${websiteTermsOfUseLink}`} target="_blank" rel="noopener noreferrer">Website Terms of Use</a>{" "}
                                        </li>
                                        {
                                            sitemapLink && (
                                                <>
                                                    <li>
                                                        <a className="divider" aria-hidden={true}>
                                                            |
                                                        </a>{" "}
                                                    </li>
                                                    <li>
                                                        <a href={`${sitemapLink}`} target="_blank" rel="noopener noreferrer">Sitemap</a>{" "}
                                                    </li>
                                                </>
                                            )
                                        }
                                        <li>
                                            {authState && authState.isAuthenticated ? (
                                                <>
                                                    &nbsp;
                                                    <span className="divider" aria-hidden={true}>
                                                        |
                                                    </span>
                                                    <OverlayTrigger
                                                        delay={{ hide: 450, show: 300 }}
                                                        overlay={renderSiteStatus}
                                                        onEnter={fetchSiteStatus}
                                                    >
                                                        <a
                                                            href={`${siteStatusLink}`}
                                                            aria-label={
                                                                !!siteStatus
                                                                    ? `Site Status: ${siteStatus}`
                                                                    : "Site Status"
                                                            }
                                                            className="siteStatus"
                                                        >
                                                            Site Status
                                                        </a>
                                                    </OverlayTrigger>
                                                </>
                                            ) : (
                                                <></>
                                            )}
                                        </li>
                                    </ul>
                                </p>
                            </li>
                            {footerParagraphOne && (
                                <li>
                                    <p>{footerParagraphOne}</p>
                                </li>
                            )}
                            {footerParagraphTwo && (
                                <li>
                                    <p>{footerParagraphTwo}</p>
                                </li>
                            )}
                        </ul>
                    </nav>
                </footer>
            )}
        </>
    );
};

export default Footer;
